import { useMemo } from "react";
import dayjs from "dayjs";
import {
  PERIOD_STATUS_DRAFT,
  defaultYearsFor,
  formatDay,
  ghostFor,
  isDefaultPeriod,
  laneOf,
  openPeriodLimitReachedIn,
  openPeriodLimitReplacementsFor,
  timeOf,
  undatedIn,
} from "helpers/periods";
import { moduleToSortValue, moduleToFallback } from "helpers/module";
import { Banner } from "components/ui";
import { usePeriodStrings } from "translation/periodManagement";

const DAY_MS = 86400000;

/** A bar narrower than this cannot hold the long ghost label. */
const WIDE_ENOUGH_PERCENT = 26;

/**
 * A period ends on the day its successor starts, so on the date axis the bars
 * touch and read as one block. Every bar gives up this much on its right edge.
 */
const BAR_GAP_PX = 2;

/** How far the selection ring (a box-shadow) sits outside the bar's own box. */
const SELECTION_RING_PX = 4;

/**
 * The ring is drawn outside the box and would otherwise close the gap again, so a
 * selected bar pulls in by the width of its ring on both sides. The silhouette
 * then lands exactly where the unselected bar ended and the gap survives.
 */
const barStyle = (leftPercent, widthPercent, selected) => {
  const ring = selected ? SELECTION_RING_PX : 0;

  return {
    left: `calc(${leftPercent}% + ${ring}px)`,
    width: `max(${BAR_GAP_PX}px, calc(${widthPercent}% - ${BAR_GAP_PX + ring * 2}px))`,
  };
};

function useDomain(periods, modules) {
  return useMemo(() => {
    const bars = modules.flatMap((module) => [
      ...laneOf(periods, module),
      ghostFor(periods, module),
    ]).filter(Boolean);

    // Lanes holding only undated periods have nothing to place. The lanes still
    // render, on this year, so those periods stay reachable (LIX-1762).
    if (bars.length === 0) {
      return modules.length === 0 ? null : {
        t0: dayjs().startOf("year").valueOf(),
        t1: dayjs().endOf("year").valueOf(),
      };
    }

    const from = Math.min(...bars.map((p) => timeOf(p.evaluation_period_start_date)));
    const to = Math.max(...bars.map((p) => timeOf(p.evaluation_period_end_date)));

    return {
      t0: dayjs(from).startOf("year").valueOf(),
      t1: dayjs(to).endOf("year").valueOf(),
    };
  }, [periods, modules]);
}

const percentIn = (domain, time) => ((time - domain.t0) / (domain.t1 - domain.t0)) * 100;

function useTicks(domain) {
  return useMemo(() => {
    if (!domain) return [];

    const first = dayjs(domain.t0).year();
    const last = dayjs(domain.t1).year();
    const step = last - first > 10 ? 2 : 1;
    const out = [];

    for (let year = first; year <= last + 1; year += step) {
      const left = percentIn(domain, dayjs(new Date(year, 0, 1)).valueOf());
      if (left < -1 || left > 101) continue;
      out.push({ year, left });
    }

    return out;
  }, [domain]);
}

export default function PeriodTimeline({
  periods,
  modules,
  selectedId,
  copyingId,
  onSelect,
  onCreate,
  today = new Date(),
}) {
  const { pt, ps } = usePeriodStrings();
  const domain = useDomain(periods, modules);
  const ticks = useTicks(domain);

  if (!domain) return null;

  const todayLeft = percentIn(domain, today.getTime());
  const primaryModule = modules[0];

  const blockedModules = modules.filter((module) => openPeriodLimitReachedIn(periods, module));

  const renderLane = (module) => {
    const bars = laneOf(periods, module);
    const ghost = ghostFor(periods, module);
    const limitReached = openPeriodLimitReachedIn(periods, module);
    const years = defaultYearsFor(module);

    const gaps = [];
    for (let i = 1; i < bars.length; i += 1) {
      const previousEnd = timeOf(bars[i - 1].evaluation_period_end_date);
      const nextStart = timeOf(bars[i].evaluation_period_start_date);
      if (nextStart - previousEnd <= 2 * DAY_MS) continue;
      const left = percentIn(domain, previousEnd);
      gaps.push({ left, width: percentIn(domain, nextStart) - left });
    }

    const undated = undatedIn(periods, module);

    return (
      <div key={module}>
        <div className={`peb-timeline__lane peb-mod--${moduleToSortValue(module)}`}>
          <div className="peb-timeline__lane-head">
            <span className="peb-module-badge">{moduleToFallback(module)}</span>
            <span className="peb-timeline__lane-duration">
              {pt(years === 1
                ? "period_management.duration_years_one"
                : "period_management.duration_years_other", { years })}
            </span>
          </div>
          <div className="peb-timeline__track">
            {ticks.map((tick) => (
              <span className="peb-timeline__gridline" key={tick.year} style={{ left: `${tick.left}%` }} />
            ))}
            {todayLeft >= 0 && todayLeft <= 100 && (
              <span className="peb-timeline__today" style={{ left: `${todayLeft}%` }} />
            )}
            {gaps.map((gap, index) => (
              <span
                className="peb-timeline__gap"
                key={index}
                style={{ left: `${gap.left}%`, width: `${gap.width}%` }}
              />
            ))}
            {bars.map((period) => {
              const left = percentIn(domain, timeOf(period.evaluation_period_start_date));
              const width = Math.max(percentIn(domain, timeOf(period.evaluation_period_end_date)) - left, 0.6);
              const copying = copyingId === period.id || period.ready === false;
              const variant = copying
                ? "copying"
                : period.status === PERIOD_STATUS_DRAFT ? "draft" : "live";

              return (
                <button
                  type="button"
                  data-testid={`period-bar-${period.id}`}
                  key={period.id}
                  className={[
                    "peb-timeline__bar",
                    `peb-timeline__bar--${variant}`,
                    period.id === selectedId ? "is-selected" : "",
                  ].filter(Boolean).join(" ")}
                  style={barStyle(left, width, period.id === selectedId)}
                  title={copying
                    ? ps("period_management.detail.copying")
                    : `${period.name} · ${formatDay(period.evaluation_period_start_date)} - ${formatDay(period.evaluation_period_end_date)}`}
                  onClick={() => onSelect(period)}
                >
                  {isDefaultPeriod(period) ? "★ " : ""}{period.name}
                </button>
              );
            })}
            {ghost && (() => {
              const left = percentIn(domain, timeOf(ghost.evaluation_period_start_date));
              const width = Math.max(percentIn(domain, timeOf(ghost.evaluation_period_end_date)) - left, 0.6);
              const range = `${formatDay(ghost.evaluation_period_start_date)} - ${formatDay(ghost.evaluation_period_end_date)}`;

              return (
                <button
                  type="button"
                  data-testid={`period-create-${module}`}
                  className={`peb-timeline__bar peb-timeline__bar--ghost ${limitReached ? "is-blocked" : ""}`}
                  style={barStyle(left, width, false)}
                  disabled={limitReached}
                  title={limitReached
                    ? ps("period_management.ghost.blocked", openPeriodLimitReplacementsFor(periods, module))
                    : ps(ghost.first ? "period_management.ghost.tooltip_first" : "period_management.ghost.tooltip", { name: ghost.name, range })}
                  onClick={() => onCreate(module)}
                >
                  {width > WIDE_ENOUGH_PERCENT
                    ? pt(ghost.first ? "period_management.ghost.create_first" : "period_management.ghost.create_next")
                    : pt("period_management.ghost.create_short")}
                </button>
              );
            })()}
          </div>
        </div>
        {undated.length > 0 && (
          <div className="peb-timeline__undated" data-testid={`period-undated-${module}`}>
            <span>{ps("period_management.lane.undated")}</span>
            {undated.map((period) => (
              <button
                type="button"
                key={period.id}
                data-testid={`period-undated-item-${period.id}`}
                className={`peb-timeline__undated-item ${period.id === selectedId ? "is-selected" : ""}`}
                title={ps("period_management.lane.undated_hint")}
                onClick={() => onSelect(period)}
              >
                {isDefaultPeriod(period) ? "★ " : ""}{period.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="peb-timeline">
      <div className="peb-timeline__legend">
        <span className={`peb-timeline__legend-item peb-mod--${moduleToSortValue(primaryModule)}`}>
          <span className="peb-timeline__swatch peb-timeline__swatch--live" />
          {pt("period_management.status.live")}
        </span>
        <span className={`peb-timeline__legend-item peb-mod--${moduleToSortValue(primaryModule)}`}>
          <span className="peb-timeline__swatch peb-timeline__swatch--draft" />
          {pt("period_management.status.draft")}
        </span>
        <span className="peb-timeline__legend-item">
          <span className="peb-timeline__swatch peb-timeline__swatch--ghost" />
          {pt("period_management.legend.proposal")}
        </span>
        <span className="peb-timeline__legend-item">
          <span className="peb-timeline__legend-star" aria-hidden="true">★</span>
          {pt("period_management.default_period")}
        </span>
        <span className="peb-timeline__legend-note">{pt("period_management.legend.archived_hint")}</span>
      </div>

      {modules.map(renderLane)}

      <div className="peb-timeline__lane peb-timeline__lane--axis">
        <div className="peb-timeline__lane-head" />
        <div className="peb-timeline__track">
          {ticks.map((tick) => (
            <span className="peb-timeline__tick" key={tick.year} style={{ left: `${tick.left}%` }}>
              {tick.year}
            </span>
          ))}
        </div>
      </div>

      {blockedModules.length > 0 && (
        <div className="peb-timeline__notes">
          {blockedModules.map((module) => (
            <Banner intent="warning" key={module} title={pt("period_management.notice_title")}>
              {pt("period_management.ghost.blocked", openPeriodLimitReplacementsFor(periods, module))}
            </Banner>
          ))}
        </div>
      )}
    </div>
  );
}
